import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import DetailsHeader from "../../components/DetailsHeader";
import {
  ensureProjectWithUsers,
  getProjectById,
  refreshForCurrentRoute,
} from "../../utils/sessionStorageLayer";
import { clearCmdraft, getCmdraft, setCmdraft } from "../../utils/storage";
import { showToast } from "../../utils/toastOptions";
import "./ChangeManagement.css";

import CreateChangeEventForm from "./CreateChangeEventForm";
import CreateCommitmentsForm from "./CreateCommitmentsForm";
import CreateRfiForm from "./CreateRfiForm";

import ContentCard from "../../components/ContentCard";
import TableCard from "../../components/TableCard";
import { apiRequest } from "../../utils/apiRequest";
import ChangeEventTable from "./ExistingChangeEventList";
import CommitmentsTable from "./ExistingCommitmentsList";
import RfiTable from "./ExistingRfiList";
import { mapBudgetCodes, mapChangeEvents, mapPatterns } from "./sharedDropdownOptions";

const steps = ["Create RFI", "Create Commitments", "Create Change Event", "Step 4", "Step 5"];

export default function ChangeManagement() {
  const { companyId: companyIdParam, projectId: projectIdParam } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const companyId = Number(companyIdParam);
  const projectId = Number(projectIdParam);

  const [project, setProject] = useState(null);
  const [projectName, setProjectName] = useState("Unknown Project");

  // Load project from session storage layer
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensureProjectWithUsers(companyId, projectId, false, false);
        if (cancelled) return;
        const foundProject = getProjectById(companyId, projectId);
        if (foundProject) {
          setProject(foundProject);
          setProjectName(foundProject.name ?? "Unknown Project");
        }
      } catch (error) {
        if (!cancelled) showToast("error", error.message || "Failed to load project");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, projectId]);

  // Load draft from sessionStorage on mount (if exists)
  const savedDraft = getCmdraft();
  const [activeStep, setActiveStep] = useState(savedDraft?.activeStep ?? 0);
  const [formData, setFormData] = useState(savedDraft?.formData ?? {});
  const [commitmentType, setCommitmentType] = useState("");
  const [openDiscardDialog, setOpenDiscardDialog] = useState(false);

  // Refresh triggers for tables
  const [refreshRfIs, setRefreshRfIs] = useState(false);
  const [refreshChangeEvents, setRefreshChangeEvents] = useState(false);
  const [refreshCommitments, setRefreshCommitments] = useState(false);

  // Single ref for current step
  const currentStepRef = useRef(null);

  // Dropdown options state
  const [rfiDropdownOptions, setRfiDropdownOptions] = useState({
    rfiManager: [],
    receivedFrom: [],
    assignees: [],
    responsibleContractor: [],
    specification: [],
    location: [],
    rfiStage: [],
    costCode: [],
    scheduleImpact: [],
    costImpact: [],
    distributionList: [],
  });

  const [changeEventDropdownOptions, setChangeEventDropdownOptions] = useState({
    status: [],
    origin: [],
    type: [],
    changeReason: [],
    scope: [],
    revenueSource: [],
    primeContract: [],
    uom: [],
    vendors: [],
    budgetCodes: [],
    contracts: [],
  });

  const [purchaseOrderDropdownOptions, setPurchaseOrderDropdownOptions] = useState({
    purchaseContractCompany: [],
    purchaseStatus: [],
    purchaseAssignedTo: [],
    budgetCodes: [],
    uom: [],
    changeEvents: [],
    patterns: [],
  });

  const [subcontractDropdownOptions, setSubcontractDropdownOptions] = useState({
    contractCompany: [],
    status: [],
    budgetCodes: [],
    changeEvents: [],
    patterns: [],
  });

  // Fetch dropdown options when project changes
  useEffect(() => {
    if (!project?.id) return;

    const fetchRfiDropdownOptions = async () => {
      try {
        if (!companyId) {
          showToast("error", "Project missing Company ID");
          return;
        }
        const data = await apiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/change-management/companies/${companyId}/projects/${project.id}/rfi-form-data/`,
          { method: "GET" }
        );

        setRfiDropdownOptions({
          rfiManager: data.rfi_managers || [],
          receivedFrom: data.received_from || [],
          assignees: data.assignees || [],
          responsibleContractor: data.responsible_contractors || [],
          specification: data.specification || [],
          location: data.locations || [],
          rfiStage: data.rfi_stages || [],
          costCode: data.cost_codes || [],
          scheduleImpact: data.schedule_impact || [],
          costImpact: data.cost_impact || [],
          distributionList: data.distributionList || [],
        });
      } catch (err) {
        showToast("error", err.message || "Failed to fetch RFI form data");
      }
    };

    const normalizeChangeEventFormData = (api) => ({
      // Status already {id,name}
      status: (api.change_event_statuses || []).map((s) => ({ id: s.id, name: s.name })),

      // Type comes as {id, change_type, show_in_select} → normalize to {id, name}
      type: (api.change_types || [])
        .filter((t) => t.show_in_select !== false)
        .map((t) => ({ id: t.id, name: t.change_type })),

      // Reasons already {id,name}
      changeReason: (api.change_reasons || []).map((r) => ({ id: r.id, name: r.name })),

      // You’re using RFIs for origin right now
      origin: (api.origins?.rfis || []).map((o) => ({ id: o.id, name: o.name })),

      // Scope and revenueSource already {value,name}
      scope: api.scope || [],
      revenueSource: api.line_item_revenue_sources || [],

      // Prime contracts (may be empty)
      primeContract: (api.prime_contracts || []).map((p) => ({
        id: p.id,
        title: p.title,
        number: p.number,
      })),

      // Vendors / contracts already good
      vendors: api.vendors || [],
      contracts: api.contracts || [],

      // Budget codes come as {id, flat_code, flat_name} → normalize to {id, code, name}
      budgetCodes: (api.budget_views || []).map((b) => ({
        id: b.id,
        code: b.flat_code || "",
        name: b.flat_name || "",
      })),

      // UoM comes as {value,name} → expose both id/abbreviation + name
      uom: (api.uom || []).map((u) => ({
        id: u.value,
        abbreviation: u.value,
        name: u.name,
      })),
    });

    const fetchChangeEventDropdownOptions = async () => {
      try {
        if (!companyId) {
          showToast("error", "Project missing Company ID");
          return;
        }
        const data = await apiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/change-management/companies/${companyId}/projects/${project.id}/change_event_form_data/`,
          { method: "GET" }
        );

        const normalized = normalizeChangeEventFormData(data);

        setChangeEventDropdownOptions(normalized);
      } catch (err) {
        showToast("error", err.message || "Failed to fetch change event form data");
      }
    };

    const fetchPurchaseOrderDropdownOptions = async () => {
      try {
        if (!companyId) {
          showToast("error", "Project missing Company ID");
          return;
        }
        const data = await apiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/commitments/companies/${companyId}/projects/${project.id}/purchase-order-form-data/`,
          { method: "GET" }
        );

        setPurchaseOrderDropdownOptions({
          purchaseContractCompany: data.vendors || [],
          purchaseStatus: data.statuses || [],
          purchaseAssignedTo: data.assignees || [],
          budgetCodes: mapBudgetCodes(data.budget_views),
          uom: data.uom || [],
          changeEvents: mapChangeEvents(data.change_events),
          patterns: mapPatterns(data.patterns),
        });
      } catch (err) {
        showToast("error", err.message || "Failed to fetch purchase order form data");
      }
    };

    const fetchSubcontractDropdownOptions = async () => {
      try {
        if (!companyId) {
          showToast("error", "Project missing Company ID");
          return;
        }
        const data = await apiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/commitments/companies/${companyId}/projects/${project.id}/subcontract-form-data/`,
          { method: "GET" }
        );

        setSubcontractDropdownOptions({
          contractCompany: data.vendors || [],
          status: data.statuses || [],
          budgetCodes: mapBudgetCodes(data.budget_views),
          changeEvents: mapChangeEvents(data.change_events),
          patterns: mapPatterns(data.patterns),
        });
      } catch (err) {
        showToast("error", err.message || "Failed to fetch subcontract form data");
      }
    };

    fetchRfiDropdownOptions();
    fetchChangeEventDropdownOptions();
    fetchPurchaseOrderDropdownOptions();
    fetchSubcontractDropdownOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, companyId]);

  const handleBackToProjects = () => {
    navigate("/change-management");
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSaveDraft = () => {
    setCmdraft({ formData, activeStep });
    showToast("success", "Draft saved!");
  };

  const handleDiscard = () => {
    setOpenDiscardDialog(true);
  };

  const handleCloseDiscardDialog = () => {
    setOpenDiscardDialog(false);
  };

  const handleConfirmDiscard = () => {
    setFormData({});
    setActiveStep(0);
    clearCmdraft();
    setOpenDiscardDialog(false);
  };

  const handleSubmit = () => {
    if (currentStepRef?.current?.handleSubmit) {
      currentStepRef.current.handleSubmit();
    } else {
      showToast("error", "No submit handler available for current step");
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <CreateRfiForm
            ref={currentStepRef}
            projectId={project?.id}
            formData={formData}
            setFormData={setFormData}
            rfiDropdownOptions={rfiDropdownOptions}
            selectedProject={project}
            setRefreshRfIs={setRefreshRfIs}
          />
        );
      case 1:
        return (
          <CreateCommitmentsForm
            ref={currentStepRef}
            projectId={project?.id}
            formData={formData}
            setFormData={setFormData}
            commitmentType={commitmentType}
            setCommitmentType={setCommitmentType}
            purchaseOrderDropdownOptions={purchaseOrderDropdownOptions}
            subcontractDropdownOptions={subcontractDropdownOptions}
            selectedProject={project}
            setRefreshCommitments={setRefreshCommitments}
          />
        );
      case 2:
        return (
          <CreateChangeEventForm
            ref={currentStepRef}
            projectId={project.id}
            formData={formData}
            setFormData={setFormData}
            changeEventDropdownOptions={changeEventDropdownOptions}
            selectedProject={project}
            setRefreshChangeEvents={setRefreshChangeEvents}
          />
        );
      case 3:
        return <Typography>Step 4 Form Placeholder</Typography>;
      case 4:
        return <Typography>Step 5 Form Placeholder</Typography>;
      default:
        return "Unknown step";
    }
  };

  if (!project) {
    return (
      <main className="page dash-wrapper">
        <Typography variant="h4">Project not found</Typography>
        <Button onClick={handleBackToProjects} startIcon={<ArrowBackIcon />}>
          Back to Projects
        </Button>
      </main>
    );
  }

  return (
    <div className="change-management-container">
      {/* Header */}
      <DetailsHeader
        title={`Project: ${projectName}`}
        onBack={handleBackToProjects}
        onRefresh={async () => {
          await refreshForCurrentRoute(pathname);
          const p = getProjectById(companyId, projectId);
          if (p) {
            setProject(p);
            setProjectName(p.name ?? "Unknown Project");
          }
          setRefreshRfIs((prev) => !prev);
          setRefreshCommitments((prev) => !prev);
          setRefreshChangeEvents((prev) => !prev);
        }}
      />

      <ContentCard elevation={3} p={3}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box sx={{ mt: 3 }}>
          {activeStep === steps.length ? (
            <Box>
              <Typography>All steps completed - you&apos;re finished</Typography>
            </Box>
          ) : (
            <Box>
              {getStepContent(activeStep)}
              <Box sx={{ display: "flex", flexDirection: "column", pt: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                  <Box sx={{ flex: "1 1 auto" }} />
                  <Button onClick={handleSaveDraft} sx={{ mr: 1 }} disabled={true}>
                    Save Draft
                  </Button>
                  <Button onClick={handleDiscard} color="error" sx={{ mr: 1 }} disabled={true}>
                    Discard Everything
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{
                      mr: 1,
                      color: "#ffffff",
                      backgroundColor: "#2e7d32",
                      "&:hover": {
                        backgroundColor: "#1b5e20",
                      },
                    }}
                  >
                    Submit
                  </Button>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                  <Button
                    color="inherit"
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    sx={{ minWidth: "40px", mr: 2 }}
                  >
                    <ArrowBackIosNewIcon />
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={activeStep === steps.length - 1}
                    sx={{
                      minWidth: "40px",
                      color: "#000000",
                      backgroundColor: "#ffffff",
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                      },
                      "&.Mui-disabled": {
                        color: "#c0c0c0",
                        backgroundColor: "#f9f9f9",
                      },
                    }}
                  >
                    <ArrowForwardIosIcon />
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </ContentCard>

      {/* Separate Paper for Tables */}
      <Box sx={{ mt: 4 }}>
        {activeStep === 0 && (
          <TableCard elevation={2} p={2} title="Existing RFI">
            <RfiTable
              projectId={project?.id}
              companyId={companyId}
              refreshTrigger={refreshRfIs}
              title="Existing RFI"
            />
          </TableCard>
        )}
        {activeStep === 1 && (
          <TableCard elevation={2} p={2} title="Existing Commitments">
            <CommitmentsTable
              projectId={project?.id}
              companyId={companyId}
              refreshTrigger={refreshCommitments}
              title="Existing Commitments"
            />
          </TableCard>
        )}
        {activeStep === 2 && (
          <TableCard elevation={2} p={2} title="Existing Change Events">
            <ChangeEventTable
              projectId={project?.id}
              companyId={companyId}
              refreshTrigger={refreshChangeEvents}
              title="Existing Change Events"
            />
          </TableCard>
        )}
      </Box>

      <Dialog open={openDiscardDialog} onClose={handleCloseDiscardDialog}>
        <DialogTitle>{"Discard all changes?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to discard all changes? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDiscardDialog}>Cancel</Button>
          <Button onClick={handleConfirmDiscard} color="error" autoFocus>
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
